import { Plus, X } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useWorkspace } from '@/app/providers/workspace-provider';
import { createContactRepository } from '@/data/repositories/contact-repository';
import type { TaskContactRole, Uuid } from '@/data/db';
import { Button } from '@/components/primitives/button';
import { Dialog } from '@/components/primitives/dialog';
import { IconButton } from '@/components/primitives/icon-button';
import { Select, TextInput } from '@/components/primitives/field';
import { cn } from '@/lib/cn';
import { useActiveContacts, useTaskContacts } from './use-contacts';

export const TASK_CONTACT_ROLE_LABELS: Record<TaskContactRole, string> = {
  reminder_target: '提醒對象',
  awaiting_reply: '等待回覆對象',
  meeting_participant: '會議參與者',
  other: '其他',
};

const NO_ROLE = '__none__';

/**
 * 任務的相關對象（spec 10.3）。
 * 顯示一律用快照，聯絡人被永久刪除後仍看得到當時的姓名。
 */
export function TaskContactList({ taskId }: { taskId: Uuid }) {
  const { db, save } = useWorkspace();
  const repository = useMemo(() => createContactRepository(db), [db]);
  const links = useTaskContacts(taskId);
  const [picking, setPicking] = useState(false);

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-[0.8125rem] font-medium text-text-muted">相關對象</h3>
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<Plus size={14} />}
          onClick={() => setPicking(true)}
        >
          加入
        </Button>
      </div>

      {links === undefined ? (
        <p className="text-[0.8125rem] text-text-muted">載入中…</p>
      ) : links.length === 0 ? (
        <p className="rounded-[var(--radius-control-lg)] bg-surface-sunken px-4 py-2.5 text-[0.8125rem] text-text-muted">
          還沒有關聯任何人。
        </p>
      ) : (
        <ul className="space-y-1.5">
          {links.map((link) => (
            <li
              key={link.id}
              className="flex items-center gap-2 rounded-[var(--radius-control-lg)] bg-surface-sunken px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-[0.875rem]">
                  {link.nameSnapshot}
                  {link.jobTitleSnapshot && (
                    <span className="ml-2 text-[0.75rem] text-text-muted">
                      {link.jobTitleSnapshot}
                    </span>
                  )}
                </p>
                {link.contactId === null && (
                  <p className="text-[0.6875rem] text-text-muted">這位聯絡人已被永久刪除</p>
                )}
              </div>

              <div className="w-36 shrink-0">
                <Select
                  aria-label={`${link.nameSnapshot} 在這件工作的角色`}
                  value={link.roleInTask ?? NO_ROLE}
                  onValueChange={(value) => {
                    if (!link.contactId) return;
                    void save.run(() =>
                      repository.linkToTask(
                        taskId,
                        link.contactId!,
                        value === NO_ROLE ? null : (value as TaskContactRole),
                      ),
                    );
                  }}
                  options={[
                    { value: NO_ROLE, label: '不指定角色' },
                    ...Object.entries(TASK_CONTACT_ROLE_LABELS).map(([value, label]) => ({
                      value,
                      label,
                    })),
                  ]}
                />
              </div>

              {link.contactId && (
                <IconButton
                  label={`移除 ${link.nameSnapshot}`}
                  tooltipSide="left"
                  icon={<X size={14} />}
                  className="size-8"
                  onClick={() =>
                    void save.run(() => repository.unlinkFromTask(taskId, link.contactId!))
                  }
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {picking && (
        <ContactPickerDialog
          taskId={taskId}
          open
          onOpenChange={setPicking}
          alreadyLinked={
            new Set((links ?? []).map((link) => link.contactId).filter(Boolean) as Uuid[])
          }
        />
      )}
    </section>
  );
}

function ContactPickerDialog({
  taskId,
  open,
  onOpenChange,
  alreadyLinked,
}: {
  taskId: Uuid;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alreadyLinked: Set<Uuid>;
}) {
  const { db, save } = useWorkspace();
  const repository = useMemo(() => createContactRepository(db), [db]);
  const contacts = useActiveContacts();
  const [keyword, setKeyword] = useState('');

  const visible = useMemo(() => {
    const needle = keyword.trim().toLowerCase();
    const all = contacts ?? [];
    if (!needle) return all.slice(0, 50);
    return all
      .filter((contact) =>
        [contact.name, contact.jobTitle, contact.organization]
          .filter((value): value is string => Boolean(value))
          .join('\n')
          .toLowerCase()
          .includes(needle),
      )
      .slice(0, 50);
  }, [contacts, keyword]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="選擇相關對象"
      description="可以多選。加入後再指定他在這件工作裡的角色。"
      size="sm"
    >
      <div className="space-y-3">
        <TextInput
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          placeholder="搜尋姓名、職稱或公司"
          aria-label="搜尋聯絡人"
        />

        {visible.length === 0 ? (
          <p className="rounded-[var(--radius-control-lg)] bg-surface-sunken px-4 py-3 text-[0.875rem] text-text-muted">
            找不到符合的聯絡人。可以先到通訊錄新增。
          </p>
        ) : (
          <ul className="max-h-72 space-y-1 overflow-y-auto">
            {visible.map((contact) => {
              const linked = alreadyLinked.has(contact.id);
              return (
                <li key={contact.id}>
                  <button
                    type="button"
                    disabled={linked}
                    onClick={() =>
                      void save.run(() => repository.linkToTask(taskId, contact.id, null))
                    }
                    className={cn(
                      'flex w-full items-center gap-2 rounded-[var(--radius-control-lg)] px-3 py-2 text-left',
                      linked ? 'opacity-40' : 'hover:bg-accent-soft',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate text-[0.875rem]">
                      {contact.name}
                      {contact.jobTitle && (
                        <span className="ml-2 text-[0.75rem] text-text-muted">
                          {contact.jobTitle}
                        </span>
                      )}
                    </span>
                    {linked && <span className="text-[0.75rem] text-text-muted">已加入</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
